import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import {
  fromEvent,
  merge,
  Observable,
  of,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { ApiAuthStorageService } from './auth-storage.service';
import { DateTime } from 'luxon';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { AuthLastActivityUpdatedEvent } from './event/last-activity-updated.class';
import { debounce } from 'lodash';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';

@Injectable()
export class ApiAuthService {
  private _loggedSubscription?: Subscription;
  private _lastActiveUpdate?: DateTime;
  private _userToken: string | null = null;

  constructor(
    private _apiSrv: ApiService,
    private _authStorage: ApiAuthStorageService,
    private _zone: NgZone,
    private _router: Router,
    @Inject(DOCUMENT) private _document: any,
    @Inject(PLATFORM_ID) private platformId: any,
    private afMessaging: AngularFireMessaging,
  ) {
    const updateLastActivity = debounce(
      () => {
        if (
          this.user &&
          (!this._lastActiveUpdate ||
            DateTime.now().diff(this._lastActiveUpdate, 'seconds').seconds > 60)
        ) {
          this._lastActiveUpdate = DateTime.now();
          this.updateLastActive().subscribe((result) => {
            if (!result.data) {
              this._router.navigateByUrl('/').then(() => this.setUser(null));
            } else {
              this.setUser(result.data);
            }
          });
        }

        this._lastActivity = DateTime.now();
        this._events.next(new AuthLastActivityUpdatedEvent(this._lastActivity));
      },
      500,
      { leading: true },
    );

    this._zone.runOutsideAngular(() => {
      merge(
        ...[
          'click',
          'load',
          'mousemove',
          'mousedown',
          'touchstart',
          'keypress',
        ].map((k) => fromEvent(this._document, k)),
      ).subscribe(updateLastActivity);
    });
  }

  private _lastActivity?: DateTime;

  get lastActivity() {
    return this._lastActivity;
  }

  private _events = new ReplaySubject<any>();

  get events() {
    return this._events;
  }

  private _userSubject = new ReplaySubject<any>();

  get userSubject() {
    return this._userSubject;
  }

  get user(): any {
    return this._authStorage.user;
  }

  get userId(): number {
    return this.user?.id;
  }

  requestNotification() {
    return new Observable<any>((observer) => {
      if (isPlatformServer(this.platformId)) {
        observer.next(null);
        observer.complete();
        return;
      }

      this.afMessaging.requestPermission.subscribe({
        next: (result) => {
          console.log('Notification permission', result);
          this.afMessaging.getToken.subscribe({
            next: (token) => {
              console.log('Notification token', token);
              this._userToken = token;
              observer.next(token);
              observer.complete();
            },
            error: (error) => {
              console.log('Notification token error', error);
              observer.next(null);
              observer.complete();
            },
          });
        },
        error: (error) => {
          console.log('Notification permission error', error);
          observer.next(null);
          observer.complete();
        },
      });
    });
  }

  changePassword(data: {
    login?: string | null;
    forgotKey?: string | null;
    password?: string | null;
    passwordConfirmation?: string | null;
  }) {
    return this._apiSrv.post('user-auth/forgot-change-password', data);
  }

  forgotPassword(data: { login?: string | null }) {
    return this._apiSrv.post('user-auth/forgot-password', data);
  }

  logged(data: { accesses?: string[] } = {}) {
    if (this.user !== undefined) {
      return of(this.user);
    }

    if (!this._loggedSubscription) {
      this._loggedSubscription = this._postAndUpdateUser(
        'user-auth/logged',
        data,
      )
        .pipe(map((result) => result.data))
        .subscribe();
    }

    return this.userSubject;
  }

  login(data: {
    login?: string | null;
    password?: string | null;
    accesses?: string[];
  }) {
    return this._postAndUpdateUser('user-auth/login', data);
  }

  logout() {
    return this._postAndUpdateUser('user-auth/logout').pipe(
      tap(() => this._router.navigate(['/auth'])),
    );
  }

  register(data: { [key: string]: any }) {
    return this._postAndUpdateUser('user-auth/register', data);
  }

  update(data: { [key: string]: any }) {
    return this._postAndUpdateUser('user-auth/update', data);
  }

  updatePassword(data: { password: string; passwordConfirmation: string }) {
    return this._postAndUpdateUser('user-auth/update-password', data);
  }

  updateLastActive() {
    return this._postAndUpdateUser('user-auth/update-last-active');
  }

  hasPermissions(permissions: string | string[] | string[][]) {
    if (!Array.isArray(permissions)) {
      permissions = [permissions];
    }
    return (
      permissions.filter((orPermissions) => {
        orPermissions = Array.isArray(orPermissions)
          ? orPermissions
          : [orPermissions];
        return !!(Object.values(this.user?.accesses) as any).find((p: string) =>
          orPermissions.includes(p),
        );
      }).length === permissions.length
    );
  }

  setUser(user: any) {
    this._authStorage.user = user;
    this._userSubject.next(user);
  }

  private _postAndUpdateUser(...args: [string, any?, any?]) {
    return new Observable<any>((observer) => {
      this.requestNotification().subscribe((result) => {
        args[1] = {
          ...args[1],
          notificationToken: result ? { type: 'web', value: result } : null,
        };
        this._apiSrv
          .post(...args)
          .pipe(tap((result) => this.setUser(result.data)))
          .subscribe(
            (result) => {
              observer.next(result);
              observer.complete();
            },
            (error) => {
              observer.error(error);
              observer.complete();
            },
          );
      });
    });
  }
}
